/**
 * Native scrollbars are painted inside their scroll container, so a click on
 * them is dispatched with the container itself as `event.target`. Outside-click
 * logic (Radix dismissable layers, flatpickr, custom `mousedown` listeners)
 * therefore reads "user clicked the modal body / the page" and dismisses the
 * layer while the user is only trying to scroll.
 *
 * The client box (`clientLeft/clientTop` + `clientWidth/clientHeight`) excludes
 * the scrollbar area, so a point inside the border box but outside the client
 * box sits on a scrollbar. `clientLeft` already accounts for RTL containers,
 * where the scrollbar is rendered on the left.
 */
export function isScrollbarPoint(
  target: EventTarget | null,
  clientX: number,
  clientY: number,
): boolean {
  if (!(target instanceof Element)) return false;

  const style = getComputedStyle(target);
  // The viewport scrollbar belongs to <html>/<body>, whose computed overflow
  // stays `visible` even though they scroll.
  const isRootElement =
    target === target.ownerDocument.documentElement ||
    target === target.ownerDocument.body;
  const canScroll = (overflow: string) =>
    isRootElement || overflow === "auto" || overflow === "scroll";

  const hasVerticalScrollbar =
    target.scrollHeight > target.clientHeight && canScroll(style.overflowY);
  const hasHorizontalScrollbar =
    target.scrollWidth > target.clientWidth && canScroll(style.overflowX);
  if (!hasVerticalScrollbar && !hasHorizontalScrollbar) return false;

  const rect = target.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  if (hasVerticalScrollbar) {
    const clientStart = rect.left + target.clientLeft;
    if (clientX < clientStart || clientX >= clientStart + target.clientWidth) {
      return true;
    }
  }

  if (hasHorizontalScrollbar) {
    const clientStart = rect.top + target.clientTop;
    if (clientY < clientStart || clientY >= clientStart + target.clientHeight) {
      return true;
    }
  }

  return false;
}

export function isScrollbarEvent(event: Event | null | undefined): boolean {
  const pointer = event as MouseEvent | undefined | null;
  if (!pointer || typeof pointer.clientX !== "number") return false;
  return isScrollbarPoint(pointer.target, pointer.clientX, pointer.clientY);
}
